
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Search, ArrowLeft, Barcode, Package2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Badge } from "@/components/ui/badge";

// Mock data for product search
const mockProducts = [
  { id: 1, code: "P001", barcode: "8901234567890", name: "Rice 5kg", price: 25.50, category: "Groceries", stock: 45, hasPromo: true },
  { id: 2, code: "P002", barcode: "8901234567891", name: "Cooking Oil 1L", price: 9.90, category: "Groceries", stock: 32, hasPromo: false },
  { id: 3, code: "P003", barcode: "8901234567892", name: "Chicken Eggs (Grade A)", price: 12.80, category: "Fresh", stock: 24, hasPromo: true },
  { id: 4, code: "P004", barcode: "8901234567893", name: "Milo 1kg", price: 23.50, category: "Beverages", stock: 18, hasPromo: false },
  { id: 5, code: "P005", barcode: "8901234567894", name: "Bread", price: 3.90, category: "Bakery", stock: 15, hasPromo: true },
];

const ProductSearch = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState(mockProducts);
  const [isSearching, setIsSearching] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSearching(true);
    
    // Simulating search delay
    setTimeout(() => {
      const results = mockProducts.filter(product => 
        product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.barcode.includes(searchQuery) ||
        product.code.includes(searchQuery)
      );
      
      setSearchResults(results);
      setIsSearching(false);
      
      if (results.length === 0) {
        toast({
          title: "No products found",
          description: "Try a different search term or check the product code/barcode",
          variant: "destructive",
        });
      }
    }, 500);
  };

  const handleSelectProduct = (product: typeof mockProducts[0]) => {
    toast({
      title: "Product selected",
      description: `${product.name} has been added to cart`,
    });
    // In a real app, you would add this product to the cart
    navigate("/dashboard/posv2");
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => navigate("/dashboard/posv2")}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-2xl font-bold">Find Product</h1>
        </div>
      </div>
      
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-md">Search by Product Name, Barcode or Product Code</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSearch} className="flex items-center space-x-2">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search products..."
                className="pl-8"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <Button type="submit" disabled={isSearching}>
              {isSearching ? "Searching..." : "Search"}
            </Button>
            <Button type="button" variant="outline" onClick={() => navigate("/dashboard/product-management")}>
              Manage Products
            </Button>
          </form>
        </CardContent>
      </Card>
      
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Product Code</TableHead>
                <TableHead>Barcode</TableHead>
                <TableHead>Product Name</TableHead>
                <TableHead>Price</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Stock</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {searchResults.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-4 text-muted-foreground">
                    No products found. Try a different search term.
                  </TableCell>
                </TableRow>
              ) : (
                searchResults.map((product) => (
                  <TableRow key={product.id}>
                    <TableCell>{product.code}</TableCell>
                    <TableCell className="flex items-center gap-1">
                      <Barcode className="h-3 w-3" />
                      {product.barcode}
                    </TableCell>
                    <TableCell className="font-medium flex items-center gap-2">
                      <Package2 className="h-4 w-4" />
                      {product.name}
                      {product.hasPromo && (
                        <Badge variant="outline" className="bg-orange-50 text-orange-700 hover:bg-orange-100 border-orange-200">
                          Promo
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>RM {product.price.toFixed(2)}</TableCell>
                    <TableCell>{product.category}</TableCell>
                    <TableCell>
                      <Badge variant={product.stock < 20 ? "destructive" : "outline"}>{product.stock}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" onClick={() => handleSelectProduct(product)}>
                        Add to Cart
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProductSearch;
